import is from '@sindresorhus/is';
import { useCallback, useRef } from 'react';

import { useMixpanelTracking } from '../../../tracking/mixpanel/use-mixpanel-tracking';
import { useJsaTrackingContext } from './use-jsa-tracking-context';

type SearchScope = 'all' | 'cluster' | 'competencies' | 'family' | 'level' | 'role' | 'skills';

type TrackSearchParams = {
    readonly query: string;
    readonly resultCount: number;
    readonly scope: SearchScope;
};

type UseTrackSearchOptions = {
    readonly architectureId?: string;
};

type UseTrackSearchResult = {
    readonly trackSearchExecuted: (params: TrackSearchParams) => void;
};

export const useTrackSearch = (options: UseTrackSearchOptions): UseTrackSearchResult => {
    const { architectureId = '' } = options;
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();
    const lastTrackedKeyRef = useRef<string>('');

    const trackSearchExecuted = useCallback(
        (params: TrackSearchParams): void => {
            const trimmedQuery = params.query.trim();

            if (!is.nonEmptyString(trimmedQuery)) {
                return;
            }

            if (is.emptyString(architectureId)) {
                return;
            }

            const trackingKey = `${params.scope}:${trimmedQuery}`;
            if (trackingKey === lastTrackedKeyRef.current) {
                return;
            }

            lastTrackedKeyRef.current = trackingKey;

            trackEvent({
                name: 'architecture-search-executed',
                payload: {
                    architectureId,
                    query: trimmedQuery,
                    resultCount: params.resultCount,
                    scope: params.scope,
                    tenantId: trackingContext.tenantId,
                    timestamp: trackingContext.timestamp,
                    userId: trackingContext.userId,
                    userRoles: trackingContext.editorRoles,
                },
            });
        },
        [trackEvent, trackingContext, architectureId],
    );

    return { trackSearchExecuted };
};
